import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiLock, FiArrowRight, FiArrowLeft, FiCheckCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import styles from '../Login/Login.module.css';
import axiosClient from '../../api/axiosClient';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('This reset link is invalid. Please request a new one.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }

    setLoading(true);
    try {
      await axiosClient.post('/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <Link to="/login" className={styles.backLink}>
        <FiArrowLeft /> Back to login
      </Link>

      <div className={styles.brandPanel}>
        <div className={styles.gridOverlay} />
        <div className={`${styles.blob} ${styles.blob1}`} />
        <div className={`${styles.blob} ${styles.blob2}`} />
        <div className={`${styles.blob} ${styles.blob3}`} />
        <div className={styles.brandContent}>
          <div className={styles.brandEyebrow}>Block Cost Technologies</div>
          <h2 className={styles.brandTitle}>
            Set a new <span>password</span>.
          </h2>
          <p className={styles.brandSub}>Choose something strong you haven't used before.</p>
        </div>
      </div>

      <div className={styles.formPanel}>
        <div className={styles.card}>
          <div className={styles.eyebrow}>BCT Platform</div>
          <h1 className={styles.title}>Reset password</h1>
          <p className={styles.subtitle}>Enter and confirm your new password below.</p>

          {error && <div className={styles.error}>{error}</div>}

          {done ? (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', color: 'var(--success)', background: 'rgba(79,174,122,.08)', border: '1px solid rgba(79,174,122,.3)', borderRadius: 12, padding: 18 }}>
              <FiCheckCircle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <b style={{ display: 'block', marginBottom: 4 }}>Password updated</b>
                <span style={{ color: 'var(--muted)', fontSize: 13.5 }}>Redirecting you to login…</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className={styles.field}>
                <label className={styles.label}>New Password</label>
                <div className={styles.inputWrap}>
                  <FiLock className={styles.inputIcon} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={styles.input}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button type="button" className={styles.eyeToggle} onClick={() => setShowPassword((v) => !v)} tabIndex={-1}>
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Confirm New Password</label>
                <div className={styles.inputWrap}>
                  <FiLock className={styles.inputIcon} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={styles.input}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <button type="button" className={styles.eyeToggle} onClick={() => setShowConfirmPassword((v) => !v)} tabIndex={-1}>
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <button type="submit" className={styles.submitBtn} disabled={loading}>
                {loading ? <span className={styles.spinner} /> : <>Update password <FiArrowRight /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
